import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION_NAME = "questionnaire";

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString() : "Unknown";
};

const normalizeLabel = (value) => {
  if (value === null || value === undefined || value === "") return "Unknown";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const buildCounts = (items, getter) => {
  const map = {};
  items.forEach((item) => {
    const label = normalizeLabel(getter(item));
    map[label] = (map[label] || 0) + 1;
  });

  return Object.entries(map)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
};

const buildGoalBuckets = (items) => {
  const buckets = {
    "0-5": 0,
    "6-10": 0,
    "11-20": 0,
    "21+": 0,
    Unknown: 0,
  };

  items.forEach((item) => {
    const raw = item.responses?.dailyGoalMinutes;
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      buckets.Unknown += 1;
      return;
    }

    if (value <= 5) buckets["0-5"] += 1;
    else if (value <= 10) buckets["6-10"] += 1;
    else if (value <= 20) buckets["11-20"] += 1;
    else buckets["21+"] += 1;
  });

  return Object.entries(buckets)
    .map(([label, count]) => ({ label, count }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);
};

function BarList({ title, data, total }) {
  if (data.length === 0) {
    return (
      <div className="card" style={{ minHeight: 140 }}>
        <h2>{title}</h2>
        <p>No responses yet.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{title}</h2>
      <div className="bar-list">
        {data.map((item) => {
          const percent = total === 0 ? 0 : Math.round((item.count / total) * 100);
          return (
            <div className="bar-row" key={`${title}-${item.label}`}>
              <div className="bar-label">{item.label}</div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${percent}%` }} />
              </div>
              <div className="bar-value">
                {item.count} ({percent}%)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionnaireResponses() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const snap = await getDocs(collection(db, COLLECTION_NAME));
        const items = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            createdAt: data.createdAt ?? null,
            responses: data.responses ?? {},
            userId: data.userId ?? "",
          };
        });

        items.sort((a, b) => {
          const aDate = toDate(a.createdAt)?.getTime() || 0;
          const bDate = toDate(b.createdAt)?.getTime() || 0;
          return bDate - aDate;
        });

        setResponses(items);
      } catch (err) {
        setError("Unable to load questionnaire responses.");
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this questionnaire response?")) return;
    setDeleteError("");
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      setResponses((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setDeleteError(err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs delete access to questionnaire responses."
        : `Delete failed: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = responses.length;
    const uniqueUsers = new Set(responses.map((item) => item.userId).filter(Boolean)).size;

    const focus = buildCounts(responses, (item) => item.responses?.focus);
    const heardFrom = buildCounts(responses, (item) => item.responses?.heardFrom);
    const level = buildCounts(responses, (item) => item.responses?.level);
    const notifications = buildCounts(responses, (item) => item.responses?.notifications);
    const dailyGoalBuckets = buildGoalBuckets(responses);

    const latest = responses[0]?.createdAt ? formatDate(responses[0].createdAt) : "Unknown";

    return {
      total,
      uniqueUsers,
      latest,
      focus,
      heardFrom,
      level,
      notifications,
      dailyGoalBuckets,
    };
  }, [responses]);

  return (
    <div className="page">
      <h1>Questionnaire Responses</h1>
      <p style={{ color: "#666" }}>
        Showing data from the "{COLLECTION_NAME}" collection.
      </p>

      {loading && <p>Loading questionnaire responses...</p>}
      {error && <p style={{ color: "#e76f51" }}>{error}</p>}
      {deleteError && <p style={{ color: "#e76f51" }}>{deleteError}</p>}

      {!loading && !error && (
        <>
          <div className="flex-wrap" style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 24 }}>
            <div className="card" style={{ flex: "1 1 220px" }}>
              <h2>Total Responses</h2>
              <p style={{ fontSize: 24, margin: 0 }}>{stats.total}</p>
            </div>
            <div className="card" style={{ flex: "1 1 220px" }}>
              <h2>Unique Users</h2>
              <p style={{ fontSize: 24, margin: 0 }}>{stats.uniqueUsers}</p>
            </div>
            <div className="card" style={{ flex: "2 1 320px" }}>
              <h2>Latest Submission</h2>
              <p style={{ fontSize: 18, margin: 0 }}>{stats.latest}</p>
            </div>
          </div>

          <div className="grid" style={{ marginBottom: 24 }}>
            <BarList title="Focus" data={stats.focus} total={stats.total} />
            <BarList title="Heard From" data={stats.heardFrom} total={stats.total} />
            <BarList title="Level" data={stats.level} total={stats.total} />
            <BarList title="Notifications" data={stats.notifications} total={stats.total} />
            <BarList title="Daily Goal (Minutes)" data={stats.dailyGoalBuckets} total={stats.total} />
          </div>

          <div className="card">
            <h2>All Responses</h2>
            {responses.length === 0 ? (
              <p>No responses yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>User</th>
                      <th>Focus</th>
                      <th>Level</th>
                      <th>Daily Goal</th>
                      <th>Heard From</th>
                      <th>Notifications</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDate(item.createdAt)}</td>
                        <td>{item.userId || "Unknown"}</td>
                        <td>{normalizeLabel(item.responses?.focus)}</td>
                        <td>{normalizeLabel(item.responses?.level)}</td>
                        <td>{normalizeLabel(item.responses?.dailyGoalMinutes)}</td>
                        <td>{normalizeLabel(item.responses?.heardFrom)}</td>
                        <td>{normalizeLabel(item.responses?.notifications)}</td>
                        <td>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={deletingId === item.id}
                            style={{
                              background: "none", border: "none", color: "#e76f51",
                              fontWeight: 600, cursor: deletingId === item.id ? "not-allowed" : "pointer",
                              opacity: deletingId === item.id ? 0.6 : 1,
                            }}
                          >
                            {deletingId === item.id ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default QuestionnaireResponses;
